import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { set, orderBy as lodashOrderByImpl, get } from "lodash";

import { useLocation, useSearchParams } from "src/router";
import type { OrderBy } from "src/graphql";

export type SortDirection = "asc" | "desc";
export const DEFAULT_TABLE_LIMIT = 50;
export const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

export type TableConfigOptions = {
  limit?: number;
  page?: number;
  setPage?: (number) => void;
  defaultSortKey?: string | null;
  defaultSortDirection?: SortDirection;
  sortOptions?: Array<string>;

  /**
   * If you pin rows to the top of the first page of the table, provide the number of pinned items
   * here to adjust the limit and offset properly.
   * @default 0
   */
  numPinnedItems?: number;
};

export type SortState = {
  sortKey: string | null;
  sortDirection: OrderBy;
};

type TableConfigResult<TOrderByType> = {
  page: number;
  limit: number;
  offset: number;
  orderBy: TOrderByType | undefined;
  sortKey: string | null;
  sortDirection: OrderBy | null;
  setPage: Dispatch<SetStateAction<number>>;
  onSort(sortKey: string): void;
  columnSortState: (key: string) => {
    sortDirection: OrderBy | null;
    onClick: () => void;
  };
};

/**
 * Determines next sort direction for a column.
 *
 * If no previous sort direction, it will derive the default sort direction.
 *
 * @param sortKey Path that will be sorted on.
 * @param prevSortDirection Previous sort direction
 * @returns {OrderBy} The direction to sort
 */
const getNextSortDirection = (prevSortDirection?: OrderBy | null) => {
  if (!prevSortDirection) {
    return "desc";
  }

  return prevSortDirection?.startsWith("asc") ? "desc" : "asc";
};

/**
 * Narrows a Hasura `OrderBy` — six values, and read unvalidated from the URL —
 * to a plain sort direction, for APIs that take one. Any `asc*` value counts as
 * ascending, matching `getNextSortDirection` and `columnSortState` so the
 * direction sent to the server agrees with the column header arrow.
 */
export const toSortDirection = (
  orderBy: OrderBy | null | undefined,
): SortDirection => (orderBy?.startsWith("asc") ? "asc" : "desc");

export const useTableConfig = <TOrderByType>({
  defaultSortKey = null,
  defaultSortDirection = DEFAULT_SORT_DIRECTION,
  sortOptions = [],
  numPinnedItems = 0,
  ...options
}: TableConfigOptions = {}): TableConfigResult<TOrderByType> => {
  // pagination
  const [page, setPage] = useState<number>(options?.page ?? 0);

  const limitForEveryPageExcept0 = options?.limit ?? DEFAULT_TABLE_LIMIT;
  const limit =
    page === 0 && numPinnedItems > 0
      ? limitForEveryPageExcept0 - numPinnedItems
      : limitForEveryPageExcept0;
  const offset = Math.max(0, limit * page - numPinnedItems);

  // sorting
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // check if key is allowed to be sorted on
  const sortKeyFromParams = searchParams.get("sortKey");
  const sortKey =
    sortKeyFromParams && sortOptions.includes(sortKeyFromParams)
      ? sortKeyFromParams
      : defaultSortKey;
  const sortDirection =
    ((searchParams.get("sortDirection") || null) as OrderBy) ||
    defaultSortDirection;

  const orderBy = useMemo(() => {
    const key = sortKey || defaultSortKey;

    if (!key) {
      return undefined;
    }

    return set<TOrderByType>({}, key, sortDirection);
  }, [sortKey, defaultSortKey, sortDirection]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const onSort = useCallback(
    (newSortKey: string) => {
      const nextSortDirection =
        newSortKey !== sortKey
          ? defaultSortDirection
          : getNextSortDirection(sortDirection);

      if (nextSortDirection) {
        searchParams.set("sortKey", newSortKey);
        searchParams.set("sortDirection", nextSortDirection);
      } else {
        searchParams.delete("sortKey");
        searchParams.delete("sortDirection");
      }

      // ensure that location state set in react-router is preserved
      setSearchParams(searchParams, { state: location.state });

      setPage(0);
    },
    [
      sortKey,
      defaultSortDirection,
      searchParams,
      sortDirection,
      location.state,
      setSearchParams,
      setPage,
    ],
  );

  const columnSortState = useCallback(
    (key: string) => ({
      sortDirection: key === sortKey ? sortDirection : null,
      onClick: () => onSort(key),
    }),
    [sortKey, sortDirection, onSort],
  );

  return {
    page: options.page || page,
    limit,
    offset,
    orderBy,
    sortKey,
    sortDirection,
    setPage: options.setPage || setPage,
    onSort,
    // Seems relatively simple, but replicating this on every column can be pretty error prone
    // Also note: we compare the sort key as opposed to looking up a key in the orderBy object
    // This is because orderBy converts keys into nested objects (eg "name.first" -> {name: {first: ...}})
    columnSortState,
  };
};

export type SortOption<Key> = {
  key: Key;
  label: string;
  direction: OrderBy;
};

export const useTableSort = <TOrderByType>(
  initial: SortOption<keyof TOrderByType>,
  options: SortOption<keyof TOrderByType>[],
): TOrderByType | undefined => {
  const [searchParams] = useSearchParams();

  const sortKeyFromParams = searchParams.get("sort");
  const sortKey =
    sortKeyFromParams &&
    options.find((option) => option.key === sortKeyFromParams)?.key;
  const sortDirection =
    ((searchParams.get("dir") || null) as OrderBy) || "desc";

  const orderBy = useMemo(() => {
    const key = (sortKey || initial.key) as any;

    if (!key) {
      return undefined;
    }

    return set<TOrderByType>({}, key, sortDirection);
  }, [sortKey, initial, sortDirection]);

  return orderBy;
};

export const lodashOrderBy = <T>(
  data: T[],
  sortKey: string | null,
  sortDirection: OrderBy | null,
  /** Allows computing a custom value to sort by.
   *
   * ```
   * lodashOrderBy(...args, {[SortKeys.Name]: (row) => row.useTraitName ? row.trait.name : row.name })
   * ```
   */
  customSortValues?: Record<string, (item: T) => any>,
): T[] => {
  if (sortKey == null || sortDirection == null || !data) {
    return data;
  }

  const key = (obj: T) => {
    let value: any;
    if (customSortValues?.[sortKey]) {
      value = customSortValues[sortKey](obj);
    } else {
      // This allows us to sort by nested properties (eg sortKey = "name.first")
      value = get(obj, sortKey);
    }

    // Lodash order by sort A-Z,a-z, so convert to lowercase
    if (typeof value === "string") {
      return value.toLowerCase();
    }

    return value;
  };

  const order = sortDirection === "asc" ? "asc" : "desc";

  return lodashOrderByImpl(data, [key], [order]);
};
